import {
  GoABadge,
  GoABlock,
  GoAButton,
  GoACallout,
  GoACircularProgress,
  GoAContainer,
  GoADivider,
  GoAIcon,
  GoASpacer,
} from '@abgov/react-components';
import React, { useEffect, useState } from 'react';

import { useIsDesktop } from '../../utils/isDesktop';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import useCsrService from '../../services/csr-api-redux';
import {
  fetchCollisionsByEmail,
  updateCollision,
} from '../../state/slices/collisionSlice';
import {
  showReportAsArchivedAfterDays,
  openCollisionValidForDays,
  fetchConfiguration,
} from '../../state/slices/configurationSlice';
import {
  getLoginInfoFromKeycloakJwt,
  isTheAccountVerifiedByGoA,
  isUserLoggedIn,
} from '../../state/slices/authSlice';
import { Collision } from '../../models/collision';
import { CollisionReportComponent } from './user-collision-report';
import { useNavigate } from 'react-router-dom';
import { downloadDamageStickerPdf } from '../../state/slices/collisionSlice';

const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

enum AccountStatus {
  Basic = 1,
  Verified = 2,
}

enum ReportStatus {
  NotYetStarted = 'Not yet started',
  InProgress = 'Report in progress',
  Submitted = 'Submitted',
  UnderReview = 'Under review',
  Approved = 'Approved',
  Rejected = 'Report rejected',
}

interface DashboardComponentProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();

export const DashboardComponent = ({}: DashboardComponentProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const csrService = useCsrService();
  const isDesktop = useIsDesktop();

  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );
  const stateAuth = useAppSelector((state: RootState) => state.auth);
  const stateCollision = useAppSelector((state: RootState) => state.collision);

  const reportStatusInProgress = 1;
  const reportStatusSubmitted = 2;
  const reportStatusUnderReview = 3;
  const reportStatusApproved = 4;
  const reportStatusRejected = 5;
  const testPoliceFileNumber = '123456789'; //TODO:Update this value & test
  const TempOpenCollisionValidForDays = '21';

  const reportNotStartedNotificationText = `You have ${TempOpenCollisionValidForDays} days to complete and submit your collision report from your report start date.`;
  const reportInProgressNotificationTextBasic = `You have ${TempOpenCollisionValidForDays} days to complete and submit your collision report from your report start date. A report not submitted within this allotted time will be automatically deleted.`;
  const reportInProgressNotificationTextVerified = `You have ${TempOpenCollisionValidForDays} days to complete and submit your collision report from your report start date.`;
  const reportSubmittedNotificationText =
    'Your collision report has been submitted';
  const reportApprovedNotificationText =
    'Your collision report has been approved.';
  const reportRejectedNotificationText =
    'Your collision report has been rejected.';

  const reportNotStartedStatusText =
    'You haven’t yet started a collision report. To start a new report, click on the link “Start a report“ below.';
  const reportInProgressStatusTextBasic =
    'You have started your report with a basic Government of Alberta account.' +
    ' To submit your final report, you must register for a verified account.';
  const reportInProgressStatusTextVerified = `Your in-progress report will be automatically deleted if not submitted within ${TempOpenCollisionValidForDays} days.`;
  const reportSubmittedStatusText =
    'Your report has been submitted and is awaiting law enforcement review.';
  const reportReviewStatusText =
    'Law enforcement is currently reviewing your submitted report.';
  const reportApprovedStatusText =
    'Your collision report has been approved by law enforcement, and the electronic damage sticker is available to download from this page.';
  const reportRejectedStatusText =
    'Law enforcement has rejected your report for the following reason(s): ';

  const [showProgress, setShowProgress] = useState(true);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [accountType, setAccountType] = useState(AccountStatus.Basic);
  const [reportStatus, setReportStatus] = useState<ReportStatus>();
  const [didFetchCollisions, setDidFetchCollisions] = useState(false);
  const [isCurrentReportAvailable, setIsCurrentReportAvailable] =
    useState(false);
  const [isArchivedReportAvailable, setIsArchivedReportAvailable] =
    useState(false);
  const [reportId, setReportId] = useState<number | undefined>(undefined);
  const [archivedReportId, setArchivedReportId] = useState<number | undefined>(
    undefined,
  );
  const [currentReport, setCurrentReport] = useState<Collision | undefined>(
    undefined,
  );
  const [archivedReport, setArchivedReport] = useState<Collision | undefined>(
    undefined,
  );
  const [isReportDisplayReady, setIsReportDisplayReady] = useState<boolean>(
    !!stateCollision.collision?.collisionType,
  );

  const [statusCalloutText, setStatusCalloutText] = useState<React.ReactNode>(
    reportNotStartedStatusText,
  );

  const [notificationText, setNotificationText] = useState<string>(
    reportNotStartedStatusText,
  );

  const calculateDaysDifference = (date: Date): number => {
    let currentDate = new Date();
    return (currentDate.getTime() - date.getTime()) / (1000 * 3600 * 24);
  };

  const handleStartReportButtonClick = () => {
    if (!isReportDisplayReady || reportStatus === ReportStatus.NotYetStarted) {
      navigate(`/collision#collision-identification`);
    }
  };

  const handleDownloadClick = (collisionId: string) => {
    if (collisionId) {
      dispatch(downloadDamageStickerPdf({ csrService, collisionId }));
    }
  };

  const navigateToTermsAndConditions = () => {
    if (isUserLoggedIn(stateAuth) && didFetchCollisions) {
      if (!stateCollision?.collision.agreementToTermsAndConditions) {
        navigate(`/collision`);
      } else if (!stateCollision?.collision.isEligible) {
        if (
          !stateCollision?.collision.eligibility ||
          stateCollision?.collision.eligibility.length === 0
        ) {
          navigate('/collision#eligibility');
        }
      }
    }
  };

  function findArchivedReport(
    collisions: Collision[],
    showReportAsArchivedAfterDays: number,
    calculateDaysDifference: (date: Date) => number,
  ): Collision | undefined {
    return collisions.find((report) => {
      const lastUpdateDate = new Date(report.updatedAt ?? '');
      const daysDifference = calculateDaysDifference(lastUpdateDate);

      const isOverThreshold = daysDifference > showReportAsArchivedAfterDays;
      const isApprovedOrRejected =
        report.reportStatus === reportStatusApproved ||
        report.reportStatus === reportStatusRejected;

      return isOverThreshold && isApprovedOrRejected;
    });
  }

  function findCurrentReport(
    collisions: Collision[],
    openCollisionValidForDays: number,
    calculateDaysDifference: (date: Date) => number,
  ): Collision | undefined {
    return collisions.find((report) => {
      const lastUpdateDate = new Date(report.updatedAt ?? '');
      const daysDifference = calculateDaysDifference(lastUpdateDate);

      if (daysDifference > openCollisionValidForDays) {
        return false;
      }

      const isApprovedOrRejected =
        report.reportStatus === reportStatusApproved ||
        report.reportStatus === reportStatusRejected;

      if (
        isApprovedOrRejected &&
        daysDifference > showReportAsArchivedAfterDays
      ) {
        return false;
      }

      return true;
    });
  }

  function syncCurrentCollisionToState(
    dispatch: AppDispatch,
    collision?: Collision,
  ) {
    if (!collision) return;
    if (
      collision.id === stateCollision.collision?.id &&
      collision.collisionType === undefined
    ) {
      return;
    }

    dispatch(updateCollision(collision));
  }

  useEffect(() => {
    if (
      stateConfiguration.configuration &&
      Array.isArray(stateConfiguration.configuration.collisionReportStatuses) &&
      stateConfiguration.configuration.collisionReportStatuses.length === 0
    ) {
      dispatch(fetchConfiguration(csrService));
    }
  }, [stateConfiguration.configuration, dispatch, csrService]);

  useEffect(() => {
    if (stateAuth.auth.access.access) {
      const info = getLoginInfoFromKeycloakJwt(stateAuth.auth.access.access);
      setUserEmail(info.email ?? '');
      setUserName(info.name ?? '');
      if (info.email === undefined || info.email === '') {
        navigate(`/`);
      } else {
        if (isTheAccountVerifiedByGoA(info.albertaVerified)) {
          setAccountType(AccountStatus.Verified);
        } else {
          setAccountType(AccountStatus.Basic);
        }
      }
    }
  }, [stateAuth]);

  const updateReportStatus = () => {
    if (!stateCollision.collision) return;

    if (!isReportDisplayReady) {
      setReportStatus(ReportStatus.NotYetStarted);
    } else {
      switch (stateCollision.collision.reportStatus) {
        case reportStatusInProgress:
          setReportStatus(ReportStatus.InProgress);
          break;
        case reportStatusSubmitted:
          setReportStatus(ReportStatus.Submitted);
          break;
        case reportStatusUnderReview:
          setReportStatus(ReportStatus.UnderReview);
          break;
        case reportStatusApproved:
          setReportStatus(ReportStatus.Approved);
          break;
        case reportStatusRejected:
          setReportStatus(ReportStatus.Rejected);
          break;
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!didFetchCollisions && userEmail) {
        try {
          const { status, data } = await dispatch(
            fetchCollisionsByEmail({
              username: userEmail,
              csrService,
              lastUpdatedWithinDays: openCollisionValidForDays,
            }),
          ).unwrap();

          setIsCurrentReportAvailable(false);
          setIsArchivedReportAvailable(false);

          if (status === 200 && data.length > 0) {
            const archived = findArchivedReport(
              data,
              showReportAsArchivedAfterDays,
              calculateDaysDifference,
            );

            if (archived) {
              setIsArchivedReportAvailable(true);
              setArchivedReportId(archived.id);
              setArchivedReport(archived);
            }

            const current = findCurrentReport(
              data,
              openCollisionValidForDays,
              calculateDaysDifference,
            );

            if (current) {
              console.log('Current report found');
              syncCurrentCollisionToState(dispatch, current);

              setIsCurrentReportAvailable(true);
              setReportId(current.id);
              setCurrentReport(current);

              setIsReportDisplayReady(!!current.collisionType);
              updateReportStatus();
            } else {
              setIsCurrentReportAvailable(false);
              setReportStatus(ReportStatus.NotYetStarted);
            }
          } else {
            setIsCurrentReportAvailable(false);
            setIsArchivedReportAvailable(false);
            setReportStatus(ReportStatus.NotYetStarted);
          }
        } catch (error) {
          setIsCurrentReportAvailable(false);
          setIsArchivedReportAvailable(false);
          setReportStatus(ReportStatus.NotYetStarted);
        } finally {
          setDidFetchCollisions(true);
          setShowProgress(false);
        }
      }
    };

    fetchData();
  }, [userEmail, didFetchCollisions]);

  useEffect(() => {
    navigateToTermsAndConditions();
  }, [userEmail, isCurrentReportAvailable, didFetchCollisions]);

  useEffect(() => {
    updateReportStatus();
    let reportNotificationText = '';

    if (reportStatus === ReportStatus.Submitted) {
      reportNotificationText = reportSubmittedNotificationText;
      setStatusCalloutText(<>{reportSubmittedStatusText}</>);
    } else if (reportStatus === ReportStatus.UnderReview) {
      reportNotificationText = reportSubmittedNotificationText;
      setStatusCalloutText(<>{reportReviewStatusText}</>);
    } else if (reportStatus === ReportStatus.Approved) {
      reportNotificationText = reportApprovedNotificationText;
      setStatusCalloutText(<>{reportApprovedStatusText}</>);
    } else if (reportStatus === ReportStatus.Rejected) {
      reportNotificationText = reportRejectedNotificationText;
      setStatusCalloutText(<>{reportRejectedStatusText}</>);

      if (currentReport && currentReport.reviewerNotes) {
        setStatusCalloutText((prev) => (
          <>
            {prev}
            {currentReport.reviewerNotes}
          </>
        ));
      }
    } else if (reportStatus === ReportStatus.InProgress) {
      if (accountType === AccountStatus.Verified) {
        reportNotificationText = reportInProgressNotificationTextVerified;
        setStatusCalloutText(<>{reportInProgressStatusTextVerified}</>);
      } else {
        reportNotificationText = reportInProgressNotificationTextBasic;
        setStatusCalloutText(<>{reportInProgressStatusTextBasic}</>);
      }
    } else {
      reportNotificationText = reportNotStartedNotificationText;
      setStatusCalloutText(<>{reportNotStartedStatusText}</>);
    }

    setNotificationText(reportNotificationText);
  }, [reportStatus, isReportDisplayReady, didFetchCollisions]);

  const getAccountTypeBadge = (): string => {
    switch (accountType) {
      case AccountStatus.Basic:
        return 'Basic account';
      case AccountStatus.Verified:
        return 'Verified account';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="dashboard-container">
      <GoACircularProgress
        variant="fullscreen"
        size="small"
        visible={showProgress}
      ></GoACircularProgress>
      <GoASpacer vSpacing="2xl" />
      <h1>Report My Collision dashboard</h1>
      <GoASpacer vSpacing="l" />
      <GoAContainer accent="thick" width="full">
        <GoABlock
          ml={isDesktop ? 'l' : 'none'}
          mr={isDesktop ? 'l' : 'none'}
          mt={isDesktop ? 'l' : 'm'}
          mb="l"
          gap={isDesktop ? 'l' : 'm'}
          direction={isDesktop ? 'row' : 'column'}
          alignment="start"
        >
          <div className="dashboard-notification-container">
            <GoABadge
              content={getAccountTypeBadge()}
              icon={true}
              type={
                accountType === AccountStatus.Basic ? 'information' : 'success'
              }
            ></GoABadge>

            {!isDesktop && <GoASpacer vSpacing="s" />}

            <div className="dashboard-name">{userName}</div>

            {!isDesktop && <GoASpacer vSpacing="s" />}

            <div className="dashboard-notification">
              <GoAIcon
                type="notifications"
                size={isDesktop ? 'large' : 'medium'}
                theme="filled"
                opacity={1}
              ></GoAIcon>
              <GoASpacer hSpacing="2xs" />
              <span className="dashboard-notification-text">
                {notificationText}
              </span>
            </div>
          </div>

          <div className="dashboard-status-container">
            <div className="dashboard-status-callout">
              <GoACallout
                type={
                  reportStatus === ReportStatus.NotYetStarted
                    ? 'important'
                    : reportStatus === ReportStatus.InProgress
                      ? 'information'
                      : reportStatus === ReportStatus.Submitted
                        ? 'information'
                        : reportStatus === ReportStatus.UnderReview
                          ? 'information'
                          : reportStatus === ReportStatus.Rejected
                            ? 'emergency'
                            : reportStatus === ReportStatus.Approved
                              ? 'success'
                              : 'important'
                }
                heading={`Status: ${
                  reportStatus === ReportStatus.Submitted ||
                  reportStatus === ReportStatus.UnderReview
                    ? 'Report ' + reportStatus
                    : reportStatus
                }`}
              >
                <div className="dashboard-status-text">
                  {statusCalloutText}

                  {reportStatus === ReportStatus.NotYetStarted ? (
                    <div
                      style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <GoAButton
                        type="tertiary"
                        size="normal"
                        trailingIcon={
                          reportStatus === ReportStatus.NotYetStarted
                            ? 'arrow-forward'
                            : 'open'
                        }
                        mt={isDesktop ? 'm' : 'm'}
                        onClick={() => {
                          handleStartReportButtonClick();
                        }}
                      >
                        {'Start a report'}
                      </GoAButton>
                    </div>
                  ) : reportStatus === ReportStatus.Approved ? (
                    <div
                      style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <GoAButton
                        type="tertiary"
                        size="normal"
                        trailingIcon="download"
                        mt={isDesktop ? 'm' : 'm'}
                        onClick={() => {
                          handleDownloadClick(
                            reportId ? reportId.toString() : '',
                          );
                        }}
                      >
                        Download damage sticker
                      </GoAButton>
                    </div>
                  ) : (
                    <GoASpacer vSpacing="3xl" />
                  )}
                </div>
              </GoACallout>
            </div>
          </div>
        </GoABlock>
      </GoAContainer>

      <GoASpacer vSpacing="l" />

      {isReportDisplayReady && (
        <CollisionReportComponent
          isArchived={false}
          reportId={reportId}
          createdDate={currentReport?.createdAt}
          lastUpdatedDate={currentReport?.updatedAt}
          reportStatus={currentReport?.reportStatus ?? reportStatusInProgress}
          policeFileNumber={testPoliceFileNumber}
          steps={currentReport?.metadata?.formState ?? []}
          collisionType={currentReport?.collisionType ?? undefined}
        />
      )}

      {isReportDisplayReady && isArchivedReportAvailable && (
        <GoADivider mt="xl" mb="xl" />
      )}

      {isArchivedReportAvailable && (
        <div>
          <CollisionReportComponent
            isArchived={isArchivedReportAvailable}
            reportId={archivedReportId}
            createdDate={archivedReport?.createdAt}
            lastUpdatedDate={archivedReport?.updatedAt}
            reportStatus={
              archivedReport?.reportStatus ?? reportStatusInProgress
            }
            policeFileNumber={testPoliceFileNumber}
          />
        </div>
      )}

      {!isReportDisplayReady && !isArchivedReportAvailable ? (
        <div>
          <div
            style={{
              font: 'var(--goa-typography-heading-m)',
            }}
          >
            Your user dashboard lets you:
          </div>
          <ul className="goa-unordered-list">
            <li>Continue a report in progress</li>
            <li>Track the status of your report</li>
            <li>Download the damage sticker once your report is approved</li>
          </ul>
        </div>
      ) : null}
    </div>
  );
};
